/**
 * Game Screen Component - Active game with chess board (v2 client-authoritative)
 *
 * Dark theme, responsive layout (portrait: vertical, landscape: horizontal split)
 * Augmentation selection modal + player info panel with click-to-detail
 *
 * Game state is managed locally via game-state-store.ts.
 * The server acts as a dumb event relay -- all game logic runs on the client.
 */

import type { Augmentation } from "@cloudnest/augmented-chess-engine";
import {
  type Color,
  type File,
  generateLegalMoves,
  getAugmentationsForPiece,
  getColorDisplayName,
  getGameResult,
  getPieceById,
  isInCheck,
  type MoveAction,
  type PossibleMove,
  type PromotablePieceType,
  type Rank,
  type Square,
} from "@cloudnest/augmented-chess-engine";
import { useEffect, useMemo, useRef, useState } from "react";
import { BoardThemed, type PieceAugmentationMap } from "../../chess-ui";
import { navigateTo } from "../../router";
import {
  EmojiMessagePanel,
  emojiMessageActions,
  handleEmojiEvent,
  resetEmojiEventState,
} from "../../shared/emoji-message";
import { useGameEventsWS } from "../../shared/hooks/use-game-events-ws";
import { isPlatformGamePath } from "../../shared/routing/game-path-mode";
import { createGameApi } from "../../shared/services/game-api";
import { getUserLabel, useAuth } from "../../shared/store/auth-store";
import { useLobby, useLobbyActions } from "../../shared/store/lobby-store";
import {
  ErrorMessage,
  GameInfoBar,
  GameResultBanner,
  PromotionDialog,
} from "./components";
import { AugmentationDetailModal } from "./components/AugmentationDetailModal";
import { MultiplayerAugmentationSelectionModal } from "./components/MultiplayerAugmentationSelectionModal";
import { MultiplayerPlayerInfoPanel } from "./components/MultiplayerPlayerInfoPanel";
import {
  gameStateActions,
  useGameState,
  useGameStateActions,
} from "./game-state-store";

interface GameScreenLocalState {
  selectedPieceId: string | null;
  legalMoves: PossibleMove[];
  pendingPromotion: {
    move: PossibleMove;
    pieceId: string;
  } | null;
  lastError: string | null;
}

function resolveChessGameApiMode() {
  if (typeof window === "undefined") return "legacy";
  return isPlatformGamePath("chess", window.location.pathname)
    ? "game-platform"
    : "legacy";
}

export function GameScreen() {
  const { user } = useAuth();
  const { currentRoom } = useLobby();
  const lobbyActions = useLobbyActions();
  const { gameState, playerColor, lastError: storeError } = useGameState();
  const gsActions = useGameStateActions();
  const roomId = currentRoom?.id ?? null;
  const chessGameApi = useMemo(
    () => createGameApi("chess", resolveChessGameApiMode()),
    [],
  );

  const [localState, setLocalState] = useState<GameScreenLocalState>({
    selectedPieceId: null,
    legalMoves: [],
    pendingPromotion: null,
    lastError: null,
  });
  const [detailAugmentation, setDetailAugmentation] =
    useState<Augmentation | null>(null);

  // Derive game info from room metadata + local game state
  const isHost = currentRoom?.hostUserId === user?.id;
  const isMyTurn = gameState?.currentPlayer === playerColor;
  const isCheck = gameState
    ? isInCheck(gameState, gameState.currentPlayer)
    : false;
  const gameResult = gameState ? getGameResult(gameState) : { ended: false };
  const isGameOver = gameResult.ended || currentRoom?.status === "COMPLETED";
  const userLabel = getUserLabel(user);
  const isAugmentationPhase = gameState?.phase === "AUGMENTATION_SELECTION";

  // Compute piece augmentation map for board glow
  const pieceAugmentations: PieceAugmentationMap = useMemo(() => {
    if (!gameState) return {};
    const map: Record<string, string[]> = {};
    for (const piece of gameState.pieces) {
      const augs = getAugmentationsForPiece(gameState, piece);
      if (augs.length > 0) {
        map[piece.id] = augs.map((a) => a.category);
      }
    }
    return map;
  }, [gameState]);

  // Ref to avoid stale closures in WS handler
  const gsActionsRef = useRef(gsActions);
  const lobbyActionsRef = useRef(lobbyActions);

  useEffect(() => {
    gsActionsRef.current = gsActions;
    lobbyActionsRef.current = lobbyActions;
  }, [gsActions, lobbyActions]);

  // Real-time game events via WebSocket
  const { connectionStatus } = useGameEventsWS(roomId, {
    onGameEvent: (eventType, payload, seq, userId) => {
      if (handleEmojiEvent(eventType, payload, userId)) return;
      gsActionsRef.current.applyRemoteEvent(eventType, payload, seq);
    },
    onRoomUpdated: (room) => {
      lobbyActionsRef.current.updateCurrentRoom(room);
    },
    onReconnect: () => {
      if (roomId) {
        gameStateActions.replayEvents(roomId).catch((error) => {
          console.error("[ws-reconnect] replay failed:", error);
        });
      }
    },
  });

  // Initialize game state on mount
  // biome-ignore lint/correctness/useExhaustiveDependencies: using primitive deps to avoid re-firing on every WS room update
  useEffect(() => {
    if (!roomId || !currentRoom || !user) return;

    const preferredColor =
      (currentRoom.config?.preferredColor as string) ?? "WHITE";
    const color: Color = isHost
      ? (preferredColor as Color)
      : preferredColor === "WHITE"
        ? "BLACK"
        : "WHITE";

    if (isHost) {
      // Try replaying events first (handles page reload).
      // If no events exist yet, initialize as host (first start).
      gameStateActions
        .replayEvents(roomId)
        .then(() => {
          const current = gameStateActions.getGameState();
          if (!current) {
            return gameStateActions.initializeAsHost(roomId, color);
          }
          gameStateActions.setPlayerInfo(roomId, color, true);
        })
        .catch((error) => {
          console.error("[game-init] host initialization failed:", error);
        });
    } else {
      // Guest waits for init event via WS / event replay
      gameStateActions.initializeAsGuest(roomId, color);
      gameStateActions.replayEvents(roomId).catch((error) => {
        console.error("[game-init] guest replay failed:", error);
      });
    }
  }, [roomId, currentRoom?.id, user?.id, isHost]);

  // Handle square click (combines piece selection and moves)
  const handleSquareClick = (file: File, rank: Rank) => {
    if (!gameState || !isMyTurn || isGameOver) return;
    if (isAugmentationPhase) return;

    const square: Square = { file, rank };

    // Check if clicking on a legal move destination
    const isLegalDestination = localState.legalMoves.some(
      (m) => m.to.file === file && m.to.rank === rank,
    );

    if (isLegalDestination) {
      handleMakeMove(square);
      return;
    }

    // Check if clicking on a piece
    const piece = gameState.pieces.find(
      (p) => p.position.file === file && p.position.rank === rank,
    );

    if (piece && piece.color === playerColor) {
      if (localState.selectedPieceId === piece.id) {
        setLocalState((s) => ({
          ...s,
          selectedPieceId: null,
          legalMoves: [],
          lastError: null,
        }));
      } else {
        const moves = generateLegalMoves(gameState, piece);
        setLocalState((s) => ({
          ...s,
          selectedPieceId: piece.id,
          legalMoves: moves,
          lastError: null,
        }));
      }
    } else {
      setLocalState((s) => ({
        ...s,
        selectedPieceId: null,
        legalMoves: [],
        lastError: null,
      }));
    }
  };

  // Handle move
  const handleMakeMove = async (to: Square) => {
    const selectedPieceId = localState.selectedPieceId;
    if (!gameState || !selectedPieceId) return;

    const piece = getPieceById(gameState, selectedPieceId);
    if (!piece) return;

    const targetMove = localState.legalMoves.find(
      (m) => m.to.file === to.file && m.to.rank === to.rank,
    );

    if (!targetMove) {
      setLocalState((s) => ({
        ...s,
        lastError: "\uC798\uBABB\uB41C \uC774\uB3D9\uC785\uB2C8\uB2E4",
      }));
      return;
    }

    if (targetMove.isPromotion) {
      setLocalState((s) => ({
        ...s,
        pendingPromotion: {
          move: targetMove,
          pieceId: selectedPieceId,
        },
        lastError: null,
      }));
      return;
    }

    await executeMove(targetMove, selectedPieceId, piece.position);
  };

  // Execute move (validate locally, submit to server as event)
  const executeMove = async (
    targetMove: PossibleMove,
    pieceId: string,
    from: Square,
    promoteTo?: PromotablePieceType,
  ) => {
    if (!gameState) return;

    const action: MoveAction = {
      type: "MOVE",
      pieceId,
      from,
      to: targetMove.to,
      capturedPieceId: targetMove.capturedPieceId,
      moveType: targetMove.moveType,
      enPassantCaptureSquare: targetMove.enPassantCaptureSquare,
      promoteTo,
      timestamp: Date.now(),
    };

    // Clear selection immediately
    setLocalState((s) => ({
      ...s,
      selectedPieceId: null,
      legalMoves: [],
      pendingPromotion: null,
      lastError: null,
    }));

    // Optimistic apply + submit (game-state-store handles rollback on failure)
    const success = await gsActions.submitAction(action);
    if (!success) {
      setLocalState((s) => ({
        ...s,
        lastError: storeError ?? "\uC774\uB3D9 \uC2E4\uD328",
      }));
    }
  };

  // Handle promotion completion
  const handleCompletePromotion = async (promoteTo: PromotablePieceType) => {
    if (!gameState || !localState.pendingPromotion) return;

    const { move, pieceId } = localState.pendingPromotion;
    const piece = getPieceById(gameState, pieceId);
    if (!piece) return;

    await executeMove(move, pieceId, piece.position, promoteTo);
  };

  // Handle resignation
  const handleResign = async () => {
    await gsActions.submitResignation(playerColor);
  };

  // Handle leave game
  const handleLeaveGame = () => {
    emojiMessageActions.clearAll();
    resetEmojiEventState();
    gsActions.reset();
    lobbyActions.leaveGame();
    navigateTo("/lobby");
  };

  if (!currentRoom || !gameState) {
    return (
      <div
        className="flex min-h-screen items-center justify-center"
        style={{
          background: "var(--cn-chess-board-frame)",
          color: "var(--cn-chess-piece-white)",
        }}
      >
        {"\uBD88\uB7EC\uC624\uB294 \uC911..."}
      </div>
    );
  }

  const whiteCaptured = gameState.capturedPieceTypes?.white?.length ?? 0;
  const blackCaptured = gameState.capturedPieceTypes?.black?.length ?? 0;
  const activeColor = gameState.currentPlayer;
  const activeLabel = activeColor === "WHITE" ? "White's turn" : "Black's turn";
  const activeBg =
    activeColor === "WHITE"
      ? "var(--cn-chess-piece-white)"
      : "var(--cn-chess-piece-black)";
  const activeText =
    activeColor === "WHITE"
      ? "var(--cn-chess-piece-black)"
      : "var(--cn-chess-piece-white)";

  return (
    <div
      data-testid="game-screen"
      className="relative flex min-h-screen flex-col items-center p-3 sm:p-6 landscape:h-dvh landscape:overflow-hidden landscape:p-2"
      style={{
        background: "var(--cn-chess-square-light)",
        color: "var(--cn-chess-piece-black)",
      }}
    >
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-3">
        <header
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl px-4 py-2.5"
          style={{
            background: "var(--cn-chess-square-light)",
            border: "1px solid var(--cn-chess-board-frame)",
          }}
          data-testid="chess-top-bar"
        >
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="leave-game-btn"
              onClick={handleLeaveGame}
              className="rounded-lg px-3 py-1.5 font-medium text-sm"
              style={{
                background: "transparent",
                border: "1px solid var(--cn-chess-board-frame)",
                color: "var(--cn-chess-piece-black)",
              }}
            >
              {"\u2190 \uB098\uAC00\uAE30"}
            </button>
            <div
              className="flex items-center gap-1.5 rounded-full px-3 py-1.5"
              style={{
                background: activeBg,
                color: activeText,
                border: "1px solid var(--cn-chess-board-frame)",
              }}
              data-testid="chess-active-player"
            >
              <span
                className="h-2 w-2 rounded-full"
                style={{ background: activeText }}
              />
              <span className="font-semibold text-sm">{activeLabel}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="flex items-center gap-1.5 rounded-full px-3 py-1"
              style={{
                background: "var(--cn-chess-piece-white)",
                color: "var(--cn-chess-piece-black)",
                border: "1px solid var(--cn-chess-board-frame)",
              }}
              data-testid="chess-score-white"
            >
              <span className="font-bold text-sm">White</span>
              <span className="font-mono text-xs">{whiteCaptured}</span>
            </div>
            <span
              className="font-mono text-xs"
              style={{ color: "var(--cn-chess-board-frame)" }}
              data-testid="move-number"
            >
              R{gameState.turnNumber}
            </span>
            <div
              className="flex items-center gap-1.5 rounded-full px-3 py-1"
              style={{
                background: "var(--cn-chess-piece-black)",
                color: "var(--cn-chess-piece-white)",
                border: "1px solid var(--cn-chess-board-frame)",
              }}
              data-testid="chess-score-black"
            >
              <span className="font-bold text-sm">Black</span>
              <span className="font-mono text-xs">{blackCaptured}</span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="h-2 w-2 rounded-full"
                style={{
                  background:
                    connectionStatus === "connected"
                      ? "var(--cn-status-success, #16a34a)"
                      : "var(--cn-status-pending, #f59e0b)",
                }}
              />
              <span
                className="text-xs"
                style={{ color: "var(--cn-chess-board-frame)" }}
              >
                {userLabel ? `${userLabel} \u00B7 ` : ""}
                {getColorDisplayName(playerColor)}
                {connectionStatus === "reconnecting"
                  ? " \u00B7 \uC7AC\uC5F0\uACB0 \uC911..."
                  : ""}
              </span>
            </div>
          </div>
        </header>

        {isGameOver && gameResult.result && (
          <GameResultBanner
            result={gameResult.result}
            reason={gameResult.reason}
            playerColor={playerColor}
          />
        )}

        <ErrorMessage message={localState.lastError ?? storeError} />

        {!isAugmentationPhase && (
          <GameInfoBar
            currentPlayer={gameState.currentPlayer}
            turnNumber={gameState.turnNumber}
            isInCheck={isCheck}
            isMyTurn={isMyTurn}
          />
        )}

        <div className="flex w-full flex-1 flex-col items-stretch gap-4 landscape:min-h-0 landscape:flex-row landscape:gap-2">
          <div
            className="chess-board-container rounded-2xl p-3 landscape:w-1/2"
            style={{
              background: "var(--cn-chess-board-frame)",
              border: "1px solid var(--cn-chess-board-frame)",
            }}
            data-testid="chess-board-frame"
          >
            <BoardThemed
              gameState={gameState}
              selectedPieceId={localState.selectedPieceId}
              legalMoves={localState.legalMoves}
              isMyTurn={isMyTurn && !isAugmentationPhase}
              gameEnded={isGameOver}
              onSquareClick={handleSquareClick}
              pieceAugmentations={pieceAugmentations}
            />
          </div>

          <div className="flex w-full flex-col gap-3 landscape:min-h-0 landscape:w-1/2 landscape:overflow-hidden">
            <MultiplayerPlayerInfoPanel
              onAugmentationClick={setDetailAugmentation}
            />

            <div className="flex w-full flex-col items-center gap-2 sm:flex-row sm:gap-3">
              {!isGameOver && !isAugmentationPhase && (
                <button
                  type="button"
                  data-testid="resign-btn"
                  onClick={handleResign}
                  className="w-full cursor-pointer rounded-md px-5 py-2 font-medium text-sm sm:w-auto"
                  style={{
                    background: "var(--cn-chess-check)",
                    color: "var(--cn-chess-piece-white)",
                    border: "none",
                  }}
                >
                  {"\uAE30\uAD8C"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Emoji Message System */}
      {roomId && (
        <EmojiMessagePanel
          gameApi={chessGameApi}
          roomId={roomId}
          displayName={userLabel}
        />
      )}

      {localState.pendingPromotion && (
        <PromotionDialog
          onSelectPiece={handleCompletePromotion}
          onCancel={() =>
            setLocalState((s) => ({
              ...s,
              pendingPromotion: null,
              selectedPieceId: null,
              legalMoves: [],
            }))
          }
        />
      )}

      <MultiplayerAugmentationSelectionModal />

      <AugmentationDetailModal
        augmentation={detailAugmentation}
        onClose={() => setDetailAugmentation(null)}
      />
    </div>
  );
}
